import { FC } from 'react';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/Form';
import InstructionHint from '@/components/ui/InstructionHint';
import MultiSelect from '@/components/ui/MultiSelect';

import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

export const AGREEMENT_OR_SETTLEMENT_CONCLUDED = {
  TOWING_COSTS: 'towing_costs',
  PARKING_COSTS: 'parking_costs',
  STANDING_COSTS: 'standing_costs',
  SUBSTITUTE_VEHICLE: 'substitute_vehicle',
  PERSONAL_INJURY: 'personal_injury',
  OTHER_DAMAGED_PROPERTY: 'other_damaged_property',
  VEHICLE_REPAIR_COSTS: 'vehicle_repair_costs',
} as const;

export const AGREEMENT_OR_SETTLEMENT_CONCLUDED_NAME =
  'agreement_or_settlement_concluded';
export const AGREEMENT_OR_SETTLEMENT_CONCLUDED_NAME_COMPLEMENTARY =
  'agreement_or_settlement_concluded_complementary';

interface Props {
  name?: string;
  isComplementary?: boolean;
}

export const SelectAgreementOrSettlementConcluded: FC<Props> = ({
  name,
  isComplementary = false,
}) => {
  const { i18n } = useLingui();
  const { control } = useFormContext();

  const fieldName =
    name ??
    (isComplementary
      ? AGREEMENT_OR_SETTLEMENT_CONCLUDED_NAME_COMPLEMENTARY
      : AGREEMENT_OR_SETTLEMENT_CONCLUDED_NAME);

  return (
    <FormField
      control={control}
      name={fieldName}
      rules={{
        required: i18n.t('Uzupełnij to pole, aby przejść dalej'),
      }}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {i18n.t('W jakim zakresie zostały zawarte ugody lub porozumienia?')}
          </FormLabel>
          <InstructionHint
            title={
              <span
                dangerouslySetInnerHTML={{
                  __html: i18n.t(
                    'Wypłata <b>kwoty bezspornej z kosztorysu</b> nie jest jednoznaczna z zawarciem ugody. Jeśli nie masz pewności, czy Twoja sprawa zakończyła się porozumieniem, zmień odpowiedź powyżej i przejdź dalej.'
                  ),
                }}
              />
            }
          />
          <FormControl>
            <MultiSelect
              {...field}
              placeholder={i18n.t('Wybierz')}
              success={field.value?.length > 0}
              data={[
                {
                  label: i18n.t('Koszty holowania'),
                  value: AGREEMENT_OR_SETTLEMENT_CONCLUDED.TOWING_COSTS,
                },
                {
                  label: i18n.t('Koszty naprawy pojazdu'),
                  value: AGREEMENT_OR_SETTLEMENT_CONCLUDED.VEHICLE_REPAIR_COSTS,
                },
                {
                  label: i18n.t('Koszty postojowe'),
                  value: AGREEMENT_OR_SETTLEMENT_CONCLUDED.STANDING_COSTS,
                },
                {
                  label: i18n.t('Koszty parkingu'),
                  value: AGREEMENT_OR_SETTLEMENT_CONCLUDED.PARKING_COSTS,
                },
                {
                  label: i18n.t('Pojazd zastępczy'),
                  value: AGREEMENT_OR_SETTLEMENT_CONCLUDED.SUBSTITUTE_VEHICLE,
                },
                {
                  label: i18n.t('Szkoda osobowa'),
                  value: AGREEMENT_OR_SETTLEMENT_CONCLUDED.PERSONAL_INJURY,
                },
                {
                  label: i18n.t(
                    'Inne uszkodzone mienie (np. fotelik, laptop, telefon)'
                  ),
                  value:
                    AGREEMENT_OR_SETTLEMENT_CONCLUDED.OTHER_DAMAGED_PROPERTY,
                },
              ]}
              data-testid="multi-select-agreement-or-settlement-concluded"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
